import React from 'react';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import debounce from 'lodash.debounce';
import {
  IncidentDataKeys,
  useSetIncidentDataChunks,
  selectedRunTimeAtom,
  incidentRuntimeAtom,
  extentStateAtom,
} from './atoms';
import { cameraSizedAtom, mapFirstLoadAtom, visualModeAtom } from '../map/atoms';
import { cameraFlightAtom } from '../map/flyToFeature';
import { parseBboxParam } from '../map/bboxParam';
import { useMatch, useSearchParams } from 'react-router-dom';
import { useCommunicationsLayer } from '../Incident/CommunicationsLayer';
import { usePopulationLayer } from '../Incident/PopulationLayer';
import { useNonResidentialStructureLayer } from '../Incident/NonResidentialLayer';
import { useResidentialStructureLayer } from '../Incident/ResidentialLayer';
import { bbox } from '@turf/turf';
import { useDeclarationsLayer } from '../Incident/DeclarationsLayer';

/**
 * The sections whose data an incident page draws besides its own: the
 * declarations section draws its grid over the population grid, so it syncs
 * and selects a population model run too (see MapControls, which mounts the
 * run selection for each of these).
 */
export const underlaySectionsFor = (sectionId: IncidentDataKeys): IncidentDataKeys[] =>
  sectionId === 'declarations' ? ['population'] : [];

export const useIncidentRuntimeDataSync = ({
  incidentId,
  sectionId,
  sync,
}: {
  incidentId: string;
  sectionId: IncidentDataKeys;
  sync: boolean;
}) => {
  const { time } = useAtomValue(selectedRunTimeAtom(`${incidentId}-${sectionId}`)) ?? {};

  // The run a fetch has finished for. Recorded rather than a plain flag so that
  // selecting a later run reports loading again, and read against `time` below
  // rather than against `hasRuntimeData`, which a run that legitimately holds no
  // rows would leave loading forever.
  const [loadedTime, setLoadedTime] = React.useState<number | undefined>(undefined);

  // Stable identity: an inline arrow here would make `callback` -- and so the
  // debounced wrapper below -- change every render, so the effect's cleanup
  // would cancel the pending fetch on every re-render. The map re-renders
  // continuously (deck.gl/view state), which previously kept the 300ms fetch
  // from ever firing, leaving the loading spinner stuck. Keying it on `time`
  // costs nothing: `callback` already changes with the selected run.
  const onLoadingFinish = React.useCallback(() => setLoadedTime(time), [time]);

  const callback = useSetIncidentDataChunks(incidentId, sectionId, time, onLoadingFinish);

  const debouncedCallback = React.useMemo(() => debounce(callback, 300), [callback]);

  const runtimeData = useAtomValue(
    incidentRuntimeAtom({
      incidentId,
      sectionId,
      lastUpdatedOn: time,
    })
  );

  const hasRuntimeData = (runtimeData?.data ?? []).length > 0;

  const fetchScheduled = sync && !hasRuntimeData && time != null;

  React.useEffect(() => {
    if (fetchScheduled) {
      debouncedCallback();
    }

    return () => {
      debouncedCallback.cancel();
    };
  }, [fetchScheduled, debouncedCallback]);

  const dataStatus: 'init' | 'loading' | 'loaded' =
    time != null && loadedTime === time ? 'loaded' : fetchScheduled ? 'loading' : 'init';

  return { dataStatus, hasRuntimeData, runtimeData };
};

export const useIncidentLayers = () => {
  const verifySection = (section?: string): IncidentDataKeys => {
    // if (incidentPageSections.every(s => s.key !== section)) {
    //   return 'population';
    // }
    return section as IncidentDataKeys;
  };

  const flyTo = useSetAtom(cameraFlightAtom);
  const visualMode = useAtomValue(visualModeAtom);
  const threeD = visualMode === '3d';
  const match = useMatch({
    path: '/:incidentId/:sectionId/:viewType/:attributeId?',
  });
  const matchNrcc = useMatch({ path: '/wowcast/:incidentId/:sectionId' });
  const params = {
    ...(match?.params ?? {}),
    ...(matchNrcc?.params ?? {}),
  };
  const isMatch = match != null || matchNrcc != null;

  const selectedSection = verifySection(params?.sectionId ?? 'population') ?? 'population';
  const incidentId = params?.incidentId ?? 'default';
  const prevIncidentRef = React.useRef<{ incidentId: string; sectionId: IncidentDataKeys } | null>(
    null
  );

  React.useEffect(() => {
    const prev = prevIncidentRef.current;
    if (prev != null && (prev.incidentId !== incidentId || prev.sectionId !== selectedSection)) {
      for (const param of Array.from(incidentRuntimeAtom.getParams())) {
        if (param.incidentId === prev.incidentId && param.sectionId === prev.sectionId) {
          incidentRuntimeAtom.remove(param);
        }
      }
    }
    prevIncidentRef.current = { incidentId, sectionId: selectedSection };
  }, [incidentId, selectedSection]);

  const [extentState, setExtentState] = useAtom(extentStateAtom);
  const [firstLoad, setFirstLoad] = useAtom(mapFirstLoadAtom);
  const cameraSized = useAtomValue(cameraSizedAtom);
  const [searchParams] = useSearchParams();

  const { dataStatus, runtimeData, hasRuntimeData } = useIncidentRuntimeDataSync({
    incidentId,
    sectionId: selectedSection,
    sync: isMatch,
  });

  const { dataStatus: populationDataStatus } = useIncidentRuntimeDataSync({
    incidentId,
    sectionId: 'population',
    sync: underlaySectionsFor(selectedSection).includes('population'),
  });

  const firstChunk = runtimeData?.data?.[0];

  const initialBbox = React.useMemo(() => {
    if (!hasRuntimeData || firstChunk == null || firstChunk.features.length === 0) {
      return null;
    }
    return bbox(firstChunk);
  }, [firstChunk, hasRuntimeData]);

  // Only a bbox the Map can honour counts; a malformed one is treated as
  // absent everywhere, or this hook would skip framing an extent nothing else
  // will frame.
  const bboxParam = searchParams.get('bbox');
  const hasBbox = React.useMemo(() => parseBboxParam(bboxParam) != null, [bboxParam]);

  // The legend's zoom-to-extent button. The trigger is cleared before the
  // fly so a fit that throws cannot leave it armed and re-throwing on every
  // render.
  React.useEffect(() => {
    if (extentState === 'zoom-to-extent') {
      setExtentState('idle');
      if (initialBbox != null && cameraSized) {
        flyTo({ bounds: initialBbox });
      }
    }
  }, [extentState, setExtentState, initialBbox, cameraSized, flyTo]);

  // The incident whose extent the camera was framed to on arrival. Every
  // arrival at an incident -- from the dashboard lists, through browser
  // history, from another incident -- frames its extent once the section's
  // data lands, and leaving the incident routes forgets it so coming back
  // frames it again; switching sections of the same incident leaves the camera
  // where the user put it. The one arrival that keeps the URL's own camera is
  // the page the app was opened on when that URL carries a bbox: a shared link
  // is the view its sender saw, and Map fits it.
  const framedIncidentRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isMatch) {
      framedIncidentRef.current = null;
      return;
    }
    if (framedIncidentRef.current === incidentId) {
      return;
    }
    if (firstLoad && hasBbox) {
      framedIncidentRef.current = incidentId;
      return;
    }
    if (initialBbox == null || !cameraSized) {
      return;
    }
    framedIncidentRef.current = incidentId;
    flyTo({ bounds: initialBbox });
    setFirstLoad(false);
  }, [isMatch, incidentId, firstLoad, hasBbox, initialBbox, cameraSized, flyTo, setFirstLoad]);

  const populationLayer = usePopulationLayer({
    id: 'population',
    visible: selectedSection === 'population',
    extruded: threeD,
    incidentId,
  });
  const communicationsLayer = useCommunicationsLayer({
    visible: selectedSection === 'communications',
    extruded: threeD,
    incidentId,
  });

  const residentialLayer = useResidentialStructureLayer({
    visible: selectedSection === 'residential',
    extruded: threeD,
    incidentId,
  });

  const nonResidentialStructuresLayer = useNonResidentialStructureLayer({
    visible: selectedSection === 'nonResidentialStructures',
    extruded: threeD,
    incidentId,
  });

  const declarationsLayer = useDeclarationsLayer({
    visible: selectedSection === 'declarations',
    extruded: threeD,
    incidentId,
  });

  const layers = React.useMemo(
    () => [
      populationLayer,
      communicationsLayer,
      residentialLayer,
      nonResidentialStructuresLayer,
      declarationsLayer,
    ],
    [
      populationLayer,
      communicationsLayer,
      residentialLayer,
      nonResidentialStructuresLayer,
      declarationsLayer,
    ]
  );

  return {
    layers,
    status: populationDataStatus !== 'init' ? populationDataStatus : dataStatus,
  };
};
